/**
 * ATL-1445 stage 3, P9 — the seam's backend-first branch: what Supabase mode does with a W write.
 *
 * Design: `docs/technical/write-cutover-stage3-app-seam.md` §1 (Supabase mode, steps 1–5), §2 (outbox
 * v2), §3 (local view), §16 (P8: the v1 → v2 migration a live write waits for), §17 (P9 as built).
 *
 * UNREACHABLE IN PRODUCTION. A session is in Supabase mode only with a capable bundle, and a bundle is
 * capable only when the compiled-in manifest shows EVERY W writer converted (`writeWCapability.ts`);
 * P9 converts the sales writers, P10–P13 the rest. The staging preview (P9b) is the one exception, on
 * staging builds only. Every organization is `writes_master='firestore'` and the setter refuses every
 * enable.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * A QUEUED WRITE (`offline: 'queue'`, every converted writer but the money-moving deletes)
 *
 *  1. `op.plan()`: ids minted, today's validation run (`INGREDIENT_NOT_FOUND`), the request CAPTURED
 *     from the DTO today's dual-write sends, the local effects built by the code the Firestore batch
 *     uses. A plan that throws rejects the write and nothing is queued — including P6's open question
 *     11, a NaN quantity: Firestore mode books the NaN as it does today, Supabase mode refuses it here
 *     (`assertLocalEffects`), the doc's recommendation.
 *  2. The entry is prepared (`queuedWriteInputOf`: the organization is the session pin's; a pin replaced
 *     meanwhile is `stale-pin`; `prepareQueuedWrite`: the store's own rules).
 *  3. If a v1 → v2 migration of the organization is copying (the first session after a flip, P8), the
 *     write either waits for it behind a visible «Синхронизация…» notice or is refused at once —
 *     `LIVE_WRITE_DURING_V1_MIGRATION`, Q-P8-5, AWAITING BEKS (below).
 *  4. The plan's `beforeAppend`, if any (review 1, S2-c: a pay-later payment's fiscal receipt), runs ONCE,
 *     only now — after every refusal above, none of which depends on it — and the entry is prepared again
 *     from the request and effects it returns. What can still refuse the write AFTER it ran is stated in
 *     §17 (the pin replaced during it, the store refusing the append, an append of unknown outcome).
 *  5. The waiter is registered (`writeWAcks.ts`) BEFORE the append, so a fast delivery is not missed.
 *     Persisted (`enqueuePrepared`), then shown (`localViewQueued`; entries a queued delete purged are
 *     `localViewDiscarded`; an append an earlier indeterminate attempt already landed is read back and
 *     shown — S3-4), then the drainer is kicked (`enqueue`).
 *  6. It resolves ONLY on the server's acknowledgement (`plan.fromAck(response)`): offline the promise
 *     waits, exactly as a native Firestore commit does today — no toast, receipt or fiscal receipt
 *     until reconnect (stage-0 §4.1). A deterministic refusal REJECTS (`WriteWHeldError`, the cashier
 *     sees the screen's error) and the entry stays HELD in the queue (invariant 14): nothing is dropped.
 *     An entry a later queued delete purged rejects with `WriteWDiscardedError` (never a success for a
 *     write that was withdrawn). An append whose outcome is unknown rejects with `WriteWMaybeQueuedError`,
 *     tells the till not to repeat the write (`maybe-queued`), and is never re-issued under another id
 *     (P2 obligation 4).
 *
 * A REFUSE-OFFLINE WRITE (`offline: 'refuse'`: a void, a cashbox delete — invariant 12)
 *
 * Never queued: sent through the drainer's `sendExclusive` — in its single-flight slot, under the drain
 * lock, and only behind an EMPTY queue, so it cannot overtake the writes made before it (a void ahead of
 * its sale). When the queue is not empty it is drained first, for at most `REFUSE_OFFLINE_SETTLE_MS`; a
 * queue that cannot empty in that time (offline) refuses the write visibly. A queue that can NEVER empty
 * without a person — a HELD entry, an unreadable or foreign record (`queue-held`, review 1 S2-a) — refuses
 * it at once, with its own notice: sync is stopped by a write that needs attention, not by connectivity.
 *
 * The send's outcome:
 *  - delivered and `applied` (`WPlan.ackDisposition`, required for a refuse-offline plan) → `fromAck`;
 *    `absent` (the document was already gone, S3-3) → `fromAbsent`; `unrecognized` → outcome unknown
 *    (review 2, S3-5: a write is this call's only when the backend says so);
 *  - a deterministic refusal, a write-master change, or a failure that proves no handler ran → a visible
 *    failure that applied nothing (`WriteWServerRefusedError`, `WriteWNotDeliveredError`). What can emit
 *    each "nothing ran" outcome for a master write (review 2, S3-4): 401 — `FirebaseAuthGuard`, before
 *    the handler; a dead backend (a 404 that is not the backend's) and a missing route (Nest's fallback
 *    404); `migration_paused` — `MigrationPauseInterceptor`, before the handler; `books_write_paused` /
 *    `books_write_state_unknown` — `assertBooksWriteMayExecute`, before the books RPC (ATL-1492). Two are
 *    classified the same way with NO backend emitter for a master write: 403 (`FirebaseAuthGuard` never
 *    emits one — `firebase-auth-status-split.atl1137.spec.ts` — and the converted routes carry no role
 *    guard) and `write_master_undetermined` (`WriteMasterGuard` admits a master write without reading the
 *    plane — it refuses only mirrors — and `WritesMasterService.assertFirestoreIsWriteMaster` guards
 *    Firestore-as-truth tools, not W routes). Both are kept: if one arrives, it came before any handler;
 *  - anything else (a timeout, a transport failure, a 5xx) is AMBIGUOUS: the backend may have committed
 *    (review 1, S2-b). The branch RESOLVES it by re-sending the identical request, behind an empty queue as
 *    before, for at most `REFUSE_OFFLINE_RESOLVE_MS` after that outcome. Only an `applied` answer
 *    (`WPlan.ackDisposition`) is this call's own: it runs `fromAck`, once. An `absent` (or unrecognised)
 *    answer to a re-send cannot be attributed — the backend answers a re-sent void exactly as it answers
 *    a sale someone else voided — so it, like a deadline with no answer, rejects with
 *    `WriteWOutcomeUnknownError` and the `outcome-unknown` notice; `fromAck` (the void's fiscal return)
 *    never runs for it. One writer call runs `fromAck`/`fromAbsent` at most once.
 */
import type { OutboxAlertSink } from './outboxAlerts'
import type { DeliveryOutcome } from './outboxClassify'
import type { ExclusiveSendResult, OutboxQueueStatus, QueueHeldBy } from './outboxDrainer'
import type { OutboxLastError } from './outboxEntry'
import {
  enqueuePrepared,
  OutboxEnqueueIndeterminateError,
  OutboxNotQueueableError,
  prepareQueuedWrite,
  queuedWriteInputOf,
  type QueueableOperation,
} from './outboxQueue'
import type { OutboxStore } from './outboxStore'
import { localViewDiscarded, localViewQueued } from './localView'
import { v1MigrationRunning, v1MigrationSettled } from './v1MigrationBarrier'
import { awaitWriteWAck, writeWAckDiscarded } from './writeWAcks'
import {
  getSessionWriteModePin,
  type SessionWriteModePin,
  type WOperation,
  type WPlan,
  type WriteWBackendFirstBranch,
  type WriteWNotice,
  type WriteWOutcomeUnknownCause,
} from './writeW'

// ============================================================================
// Q-P8-5 — AWAITING BEKS
// ============================================================================

/**
 * Q-P8-5 (§16, open): the FIRST live write of the first session after a flip may meet the v1 → v2
 * migration still copying (bounded lock waits of `V1_EXCLUSIVE_WAIT_MS` = 90 s, then an unbounded copy
 * of at most 100 appends, paged after 150 s). The migrated writes must go FIRST (they are older), so a
 * live write cannot simply be appended ahead of them.
 *
 *  - `'wait'` — the doc's default, BUILT and ON: the write waits for the migration (`enqueuePrepared`
 *    already does), behind a visible «Синхронизация…» notice. The cart was cleared before the write, so
 *    the cashier can keep working; the sale's own confirmation (and receipt) waits.
 *  - `'refuse'` — the alternative, BUILT and OFF: the write is refused at once with the same notice,
 *    nothing queued; the cashier repeats it once the migration is over (normally seconds).
 *
 * One constant, so the decision is one line. AWAITING BEKS: whether the first sale of a flip session
 * may wait that long.
 */
export type LiveWriteDuringV1Migration = 'wait' | 'refuse'
export const LIVE_WRITE_DURING_V1_MIGRATION: LiveWriteDuringV1Migration = 'wait'

/** Invariant 12: how long a refuse-offline write waits for the queue in front of it to drain. */
export const REFUSE_OFFLINE_SETTLE_MS = 15_000

/**
 * Review 1, S2-b: how long, after an AMBIGUOUS outcome, a refuse-offline write keeps re-sending to learn
 * what happened. Measured from that outcome, not from the write's start: the first send may itself have
 * waited out the 60 s write timeout, long past `REFUSE_OFFLINE_SETTLE_MS`. A re-send started before the
 * deadline is awaited to its end (a request cannot be withdrawn); note the backend answers a void of an
 * already-voided sale only after its own ~15 s not-found retry loop (`SalesService.deleteSale`).
 */
export const REFUSE_OFFLINE_RESOLVE_MS = 15_000

/** While waiting for the queue in front of a refuse-offline write, how often it is looked at again. */
export const REFUSE_OFFLINE_RECHECK_MS = 1_000

/** Between two re-sends of an ambiguous refuse-offline write. */
export const REFUSE_OFFLINE_RESEND_PAUSE_MS = 1_000

/**
 * Retry outcomes that PROVE no handler ran, so nothing was applied (review 1, S2-b): a 401 (`FirebaseAuthGuard`),
 * a dead backend or a missing route. `forbidden` and `master-undetermined` have no backend emitter for a
 * master write (review 2, S3-4: the auth guard never answers 403; `WriteMasterGuard` refuses only mirrors);
 * both are kept, as either would be a refusal before any handler.
 */
const NOT_EXECUTED_REASONS: ReadonlySet<string> = new Set(['unauthorized', 'forbidden', 'master-undetermined', 'dead-backend', 'route-not-found'])
/**
 * Refusal codes that PROVE nothing executed: the migration-pause interceptor answers a master write
 * before the handler (`migration_paused`), and `assertBooksWriteMayExecute` refuses before the books
 * RPC (`books_write_paused`, `books_write_state_unknown`; ATL-1492: "nothing has executed").
 */
const NOT_EXECUTED_CODES: ReadonlySet<string> = new Set(['migration_paused', 'books_write_paused', 'books_write_state_unknown'])

// ============================================================================
// Errors
// ============================================================================

/** The backend refused the write deterministically; the entry is HELD on this device (invariant 14). */
export class WriteWHeldError extends Error {
  readonly code = 'WRITE_W_HELD'
  readonly status: number | null
  readonly refusalCode: string | null
  constructor(
    readonly label: string,
    lastError: OutboxLastError | null,
  ) {
    super(`the server refused ${label}; it is held on this device until a person resolves it`)
    this.name = 'WriteWHeldError'
    this.status = lastError?.status ?? null
    this.refusalCode = lastError?.code ?? null
  }
}

/**
 * The queued write was removed WITHOUT being delivered: a later queued delete of the same document
 * superseded it (`planDeletePurge`). Its effect is withdrawn; the writer must not report success.
 */
export class WriteWDiscardedError extends Error {
  readonly code = 'WRITE_W_DISCARDED'
  constructor(readonly label: string) {
    super(`${label} was withdrawn before it was sent (a later delete of the same document superseded it)`)
    this.name = 'WriteWDiscardedError'
  }
}

/** The append's outcome is unknown: the write MAY be queued. Never re-issued under another id. */
export class WriteWMaybeQueuedError extends Error {
  readonly code = 'WRITE_W_MAYBE_QUEUED'
  constructor(
    readonly label: string,
    readonly entryId: string,
  ) {
    super(`${label} may or may not be queued (entry ${entryId}); it is not repeated automatically`)
    this.name = 'WriteWMaybeQueuedError'
  }
}

/** Refused before anything was sent or queued (a refuse-offline write, or Q-P8-5's `'refuse'`). */
export class WriteWNotSentError extends Error {
  readonly code = 'WRITE_W_NOT_SENT'
  constructor(
    readonly label: string,
    readonly why: Extract<ExclusiveSendResult, { kind: 'not-sent' }>['why'] | 'no-drainer' | 'v1-migration',
    readonly depth: number | null = null,
    /** `queue-held` only: what stops the queue (review 1, S2-a). */
    readonly stoppedBy: QueueHeldBy | null = null,
  ) {
    super(`${label} was not sent: ${why}${stoppedBy ? ` (${stoppedBy})` : ''}`)
    this.name = 'WriteWNotSentError'
  }
}

/**
 * Review 1, S2-b: a refuse-offline write was SENT, may have applied, and could not be confirmed. Nothing
 * of the writer's own tail ran (for a void: no Updates record, no audit, NO FISCAL RETURN). `cause`:
 *  - `unanswered`: no definite answer before `REFUSE_OFFLINE_RESOLVE_MS` (offline, timeouts, 5xx), or the
 *    re-sends could not go out (the queue, the session);
 *  - `unattributable`: a re-send found the document already GONE — the write is applied, but the answer
 *    does not say whether by this call's earlier attempt or by someone else, so the tail (a fiscal
 *    return) cannot be run without risking a second one;
 *  - `unrecognized` (review 2, S3-5): a 2xx the plan's `ackDisposition` does not recognise, first or
 *    re-sent — the backend did not say this request applied it.
 */
export class WriteWOutcomeUnknownError extends Error {
  readonly code = 'WRITE_W_OUTCOME_UNKNOWN'
  constructor(
    readonly label: string,
    readonly cause: WriteWOutcomeUnknownCause,
    /** The ambiguous outcome that started the resolution. */
    readonly reason: string,
    readonly status: number | null,
  ) {
    super(`${label} may have been applied (${cause}; first outcome ${reason}); its tail did not run`)
    this.name = 'WriteWOutcomeUnknownError'
  }
}

/** A refuse-offline write was sent and did not land (transport, timeout, 5xx, 401/403): not queued. */
export class WriteWNotDeliveredError extends Error {
  readonly code = 'WRITE_W_NOT_DELIVERED'
  constructor(
    readonly label: string,
    readonly reason: string,
    readonly status: number | null,
  ) {
    super(`${label} did not reach the server (${reason}); nothing was queued`)
    this.name = 'WriteWNotDeliveredError'
  }
}

/** A refuse-offline write the backend refused deterministically (nothing to hold: it was never queued). */
export class WriteWServerRefusedError extends Error {
  readonly code = 'WRITE_W_SERVER_REFUSED'
  constructor(
    readonly label: string,
    readonly status: number | null,
    readonly refusalCode: string | null,
  ) {
    super(`the server refused ${label}${status === null ? '' : ` (${status})`}`)
    this.name = 'WriteWServerRefusedError'
  }
}

// ============================================================================
// The branch
// ============================================================================

export interface BackendFirstDeps {
  /** The app's outbox store (opened per call: P2 obligation 8). */
  openStore: () => OutboxStore
  sink: Pick<OutboxAlertSink, 'deletePurge' | 'enqueueUnresolved'>
  /** Kick the session drainer of the organization (starting it if the session has none); false when none can run. */
  kickDrainer: (orgFirestoreId: string) => boolean
  /** The session drainer's exclusive send, or null when the session has no drainer for the organization. */
  sendExclusive: (orgFirestoreId: string) => ((request: WPlan<unknown>['request'], options: { kind: WOperation<unknown>['kind']; label: string }) => Promise<ExclusiveSendResult>) | null
  /** Subscribe to the session drainer's queue status (to wait for an empty queue); null without a drainer. */
  subscribeQueue: (orgFirestoreId: string, listener: (status: OutboxQueueStatus) => void) => (() => void) | null
  bundleId: () => string | null
  now: () => number
  notice: (notice: WriteWNotice) => void
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
  /** Q-P8-5 (injectable for tests; the app uses `LIVE_WRITE_DURING_V1_MIGRATION`). */
  v1MigrationPolicy?: LiveWriteDuringV1Migration
  settleMs?: number
  /** Injectable for tests; the app uses `REFUSE_OFFLINE_RESOLVE_MS` / `REFUSE_OFFLINE_RECHECK_MS`. */
  resolveMs?: number
  recheckMs?: number
}

/** The operation and its plan as the queue takes them; only an `offline: 'queue'` operation qualifies. */
const queueableOf = <R>(op: WOperation<R>, plan: WPlan<R>): QueueableOperation => {
  // A `.js` caller is not held to the type: the queue's own check refuses anything but 'queue' too.
  if (op.offline !== 'queue') throw new OutboxNotQueueableError('offline-refuse', op.label)
  return {
    label: op.label,
    kind: op.kind,
    offline: op.offline,
    documentId: plan.documentId,
    relatedIds: plan.relatedIds,
    request: plan.request,
    effects: plan.effects,
  }
}

export function createBackendFirstBranch(deps: BackendFirstDeps): WriteWBackendFirstBranch {
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms))
  const clearTimer = deps.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const settleMs = deps.settleMs ?? REFUSE_OFFLINE_SETTLE_MS
  const resolveMs = deps.resolveMs ?? REFUSE_OFFLINE_RESOLVE_MS
  const recheckMs = deps.recheckMs ?? REFUSE_OFFLINE_RECHECK_MS
  const policy = deps.v1MigrationPolicy ?? LIVE_WRITE_DURING_V1_MIGRATION

  async function queued<R>(op: WOperation<R>, pin: SessionWriteModePin): Promise<R> {
    const org = pin.decision.organizationId
    const plan = await op.plan()
    const session = { pin, enqueuedAt: deps.now(), bundleId: deps.bundleId() }
    // The pin must still be the one in force: a sign-out into another organization during the plan
    // makes `queuedWriteInputOf` refuse (`stale-pin`), never queue under the wrong organization. The
    // request is checked against the store's own rules here, BEFORE any side effect (S2-c).
    let prepared = prepareQueuedWrite(queuedWriteInputOf(queueableOf(op, plan), session))
    if (v1MigrationRunning(org)) {
      deps.notice({ kind: 'syncing', label: op.label })
      if (policy === 'refuse') throw new WriteWNotSentError(op.label, 'v1-migration')
    }
    if (plan.beforeAppend) {
      // Review 1, S2-c: the side effect (a payment's fiscal receipt) runs only now, once, and the entry
      // persisted is the one that carries its result. What can still refuse after it is §17's residual.
      const final = await plan.beforeAppend()
      prepared = prepareQueuedWrite(queuedWriteInputOf(queueableOf(op, { ...plan, request: final.request, effects: final.effects ?? null }), session))
    }
    const ack = awaitWriteWAck(org, prepared.entry.entryId)
    const store = deps.openStore()
    let result: Awaited<ReturnType<typeof enqueuePrepared>>
    try {
      result = await enqueuePrepared(store, prepared, deps.sink)
    } catch (error) {
      ack.cancel()
      if (error instanceof OutboxEnqueueIndeterminateError) {
        // S3-5: it may be queued; a repeat would be a second write. Tell the till so.
        deps.notice({ kind: 'maybe-queued', label: op.label })
        throw new WriteWMaybeQueuedError(op.label, prepared.entry.entryId)
      }
      throw error
    }
    ack.markQueued()
    if (result.status === 'queued') {
      localViewQueued(result.entry)
      if (result.purged.length > 0) {
        const purged = result.purged.map((e) => e.entryId)
        localViewDiscarded(org, purged)
        // The writes this delete superseded will never be delivered: their writers learn it now.
        writeWAckDiscarded(org, purged)
      }
    } else {
      // S3-4: an earlier attempt of this append landed although it reported an unknown outcome. Read the
      // entry back so it shows now, exactly as a fresh append does; `null` means it is already gone (a
      // drain delivered it, and its waiter has the answer). A delete's purge in that earlier attempt is
      // not reported by the store (its result was lost): §17's residual.
      let landed: Awaited<ReturnType<OutboxStore['get']>> = null
      try {
        landed = await store.get(org, result.entryId)
      } catch {
        // Not shown yet; the drainer's next listing (kicked below) installs it in the local view.
      }
      if (landed) localViewQueued(landed)
    }
    deps.kickDrainer(org)
    const outcome = await ack.promise
    if (outcome.kind === 'held') throw new WriteWHeldError(op.label, outcome.lastError)
    if (outcome.kind === 'discarded') throw new WriteWDiscardedError(op.label)
    return plan.fromAck(outcome.response)
  }

  /** Resolves when the session's queue reads empty, or after `ms` (false), whichever is first. */
  function queueEmptied(org: string, ms: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let done = false
      let unsubscribe: (() => void) | null = null
      const finish = (empty: boolean): void => {
        if (done) return
        done = true
        clearTimer(handle)
        unsubscribe?.()
        unsubscribe = null
        resolve(empty)
      }
      const handle = setTimer(() => finish(false), ms)
      const subscribed = deps.subscribeQueue(org, (status) => {
        if (status.known && status.depth === 0) finish(true)
      })
      // S3-1: `subscribeQueue` may call the listener synchronously (the session's does, with the current
      // status), before it has returned the unsubscribe — so a finish that happened inside it could not
      // unsubscribe. Whatever finished, the subscription is released here.
      if (!subscribed) finish(false)
      else if (done) subscribed()
      else unsubscribe = subscribed
    })
  }

  const pause = (ms: number): Promise<void> => new Promise<void>((resolve) => setTimer(resolve, ms))

  /** The send's outcome proves no handler ran: nothing was applied (S2-b). */
  const notExecuted = (outcome: Extract<DeliveryOutcome, { kind: 'retry' }>): boolean =>
    NOT_EXECUTED_REASONS.has(outcome.reason) || (outcome.code !== null && NOT_EXECUTED_CODES.has(outcome.code.toLowerCase()))

  async function refuseOffline<R>(op: WOperation<R>, pin: SessionWriteModePin): Promise<R> {
    const org = pin.decision.organizationId
    const plan = await op.plan()
    // Review 2, S3-5: without a way to read its answers, no 2xx would be attributable to this call — so a
    // refuse-offline plan must declare one; one that does not is refused before anything is sent.
    const ackDisposition = plan.ackDisposition
    if (!ackDisposition) throw new Error(`${op.label}: a refuse-offline plan must declare ackDisposition`)
    if (v1MigrationRunning(org)) {
      deps.notice({ kind: 'syncing', label: op.label })
      await v1MigrationSettled(org)
    }
    let deadline = deps.now() + settleMs
    /** Set by the first AMBIGUOUS outcome (S2-b): from then on, only an `applied` answer resolves. */
    let unresolved: { reason: string; status: number | null } | null = null
    /** The drainer is kicked at most once per write (review 2, S3-3). */
    let kicked = false
    const outcomeUnknown = (cause: WriteWOutcomeUnknownCause, answered?: number | null): WriteWOutcomeUnknownError => {
      const first = unresolved ?? { reason: cause === 'unrecognized' ? 'unrecognized-answer' : 'unknown', status: answered ?? null }
      deps.notice({ kind: 'outcome-unknown', label: op.label, cause })
      return new WriteWOutcomeUnknownError(op.label, cause, first.reason, first.status)
    }
    for (;;) {
      // The pin must still be the one in force (a sign-out into another organization while this waited):
      // the backend resolves the organization from the caller's credentials, not from the request.
      if (getSessionWriteModePin() !== pin) {
        if (unresolved) throw outcomeUnknown('unanswered')
        throw new OutboxNotQueueableError('stale-pin', op.label)
      }
      const send = deps.sendExclusive(org)
      if (!send) {
        if (unresolved) throw outcomeUnknown('unanswered')
        // S3-2: the same visible notice as every other refusal to send.
        deps.notice({ kind: 'not-sent', label: op.label, why: 'no-drainer' })
        throw new WriteWNotSentError(op.label, 'no-drainer')
      }
      const result = await send(plan.request, { kind: op.kind, label: op.label })
      if (result.kind === 'sent') {
        const outcome = result.outcome
        if (outcome.kind === 'delivered') {
          const disposition = outcome.detail === 'entity-absent-delete' ? 'absent' : ackDisposition(result.response)
          // Only the backend saying THIS request applied it makes the write this call's (S3-5).
          if (disposition === 'applied') return plan.fromAck(result.response)
          if (disposition === 'unrecognized') throw outcomeUnknown('unrecognized', 200)
          // S3-3: the FIRST answer says the document was already gone — not this call's write. After an
          // ambiguous attempt the same answer cannot be attributed (this call's earlier attempt, or not).
          if (!unresolved) return (plan.fromAbsent ?? plan.fromAck)(result.response)
          throw outcomeUnknown('unattributable')
        }
        if (!unresolved) {
          if (outcome.kind === 'held' || outcome.kind === 'master-changed') throw new WriteWServerRefusedError(op.label, outcome.status, outcome.code)
          if (notExecuted(outcome)) throw new WriteWNotDeliveredError(op.label, outcome.reason, outcome.status)
          // AMBIGUOUS: the backend may have committed. Resolve it by re-sending the identical request.
          unresolved = { reason: outcome.reason, status: outcome.status }
          deadline = deps.now() + resolveMs
        } else if (outcome.kind === 'held' || outcome.kind === 'master-changed') {
          // A re-send refused deterministically (or the drainer paused): no later re-send can do better.
          throw outcomeUnknown('unanswered')
        }
        const left = deadline - deps.now()
        if (left <= 0) throw outcomeUnknown('unanswered')
        await pause(Math.min(left, REFUSE_OFFLINE_RESEND_PAUSE_MS))
        continue
      }
      const left = deadline - deps.now()
      const retryable = result.why === 'queue-not-empty' || result.why === 'other-tab' || result.why === 'migrating'
      if (!retryable || left <= 0) {
        if (unresolved) throw outcomeUnknown('unanswered')
        deps.notice({ kind: 'not-sent', label: op.label, why: result.why })
        throw new WriteWNotSentError(op.label, result.why, result.depth, result.why === 'queue-held' ? result.stoppedBy : null)
      }
      if (result.why === 'queue-not-empty') {
        // Deliver what is in front of it first; offline, this times out and the write is refused. The
        // drainer is kicked ONCE per write (review 2, S3-3): an `enqueue` kick releases the head's backoff
        // and re-sends it at once, so a kick per recheck would hammer a failing backend ~15 times per void
        // and push the head's backoff to `RETRY_CAP_MS`. After the kick the drainer's own schedule
        // delivers; the rechecks every `REFUSE_OFFLINE_RECHECK_MS` only re-read the queue, so a queue that
        // becomes HELD meanwhile is refused then (S2-a), not at the deadline.
        if (!kicked) {
          kicked = true
          deps.kickDrainer(org)
        }
        await queueEmptied(org, Math.min(left, recheckMs))
      } else {
        await pause(Math.min(left, 500))
      }
    }
  }

  return {
    run<R>(op: WOperation<R>, pin: SessionWriteModePin): Promise<R> {
      // A `.js` caller is not held to the type: anything but 'refuse' is refused by the queue's own check.
      return op.offline === 'refuse' ? refuseOffline(op, pin) : queued(op, pin)
    },
  }
}
